import { createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

import {
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/free-regular-svg-icons';
import {
  faExclamationCircle,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosResponse, isAxiosError } from 'axios';
import moment from 'moment';
import qs from 'qs';
import httpRequest from 'src/services/httpRequest';
import Url from 'url-parse';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { SettingFormDataTypes } from 'src/components/SettingsTab/SettingsTab';

import { SortOrder } from 'src/models/GalenData/SortOrder.model';
import {
  CompletedReasonEnum,
  Session,
  transformExamToSession,
} from 'src/models/Session.model';

import { AddUserRequestBody } from './GalenData/AddUserRequestBody.model';
import { BackendStatus } from './GalenData/BackendStatus.model';
import {
  CriteriaGroupGroupElementsInner,
  CriteriaGroupGroupElementsInnerOperatorEnum,
} from './GalenData/CriteriaGroupGroupElementsInner.model';
import { CustomDataRequestBody } from './GalenData/CustomDataRequestBody';
import {
  CustomField,
  CustomFieldAreaEnum,
  CustomPatientProfileFieldEnum,
  CustomPracticeProfileFieldEnum,
  CustomUserProfileFieldEnum,
} from './GalenData/CustomField.model';
import { CustomFieldData } from './GalenData/CustomFieldData.model';
import { DeviceDataAggregateRequestBody } from './GalenData/DeviceDataAggregateRequestBody.model';
import { DeviceDataGetFilter } from './GalenData/DeviceDataGetFilter.model';
import { DeviceDataGetRequestBody } from './GalenData/DeviceDataGetRequestBody.model';
import { DeviceDataGetRequestBodyCriteriaGroup } from './GalenData/DeviceDataGetRequestBodyCriteriaGroup.model';
import { DeviceDataMultiplePropertiesAggregateRequestBody } from './GalenData/DeviceDataMultiplePropertiesAggregateRequestBody.model';
import { DeviceDataSaveRequestBody } from './GalenData/DeviceDataSaveRequestBody.model';
import { DeviceDataView, ExamData } from './GalenData/DeviceDataView.model';
import { getByFiltersUsingGET9 } from './GalenData/getByFiltersUsingGET9.model';
import { getByOrgFilter } from './GalenData/getByOrgFilter.model';
import {
  updatePracticeSettings,
  updateUserSettings,
} from './GalenData/helpers/saveSettings';
import {
  LoginUsingPOST,
  Oauth2LoginPost,
  SendMFACodePost,
} from './GalenData/LoginUsingPOST.model';
import {
  OrderData,
  OrderStatusEnum,
  OrderSubmissionData,
  OrderTaskId,
  SiteData,
} from './GalenData/Order.model';
import { OwnerFilter } from './GalenData/OwnerFilter.model';
import { PageOfCustomField } from './GalenData/PageOfCustomField.model';
import {
  AboutData,
  PageOfDeviceData,
} from './GalenData/PageOfDeviceData.model';
import { PageOfDeviceDataAggregateResult } from './GalenData/PageOfDeviceDataAggregateResult.model';
import { PageOfDeviceDataMultipleAggregate } from './GalenData/PageOfDeviceDataMultipleAggregate.model';
import { PageOfDeviceDataView } from './GalenData/PageOfDeviceDataView.model';
import { PageOfPractice } from './GalenData/PageOfPractice.model';
import { PageOfUser } from './GalenData/PageOfUser.model';
import { Practice } from './GalenData/Practice.model';
import { PracticeAssociation } from './GalenData/PracticeAssociation.model';
import {
  ApiUrlsEnum,
  AxiosResponseWithRefreshToken,
  ContentTypeEnum,
  IGetUserAdvancedParams,
  IGetUsersAdvancedRequestBody,
} from './GalenData/Request.model';
import { UpdatePasswordRequestBody } from './GalenData/UpdatePasswordRequestBody.model';
import { GalenDataUser } from './GalenData/User.model';
import { MFAModeEnum, UserMFA } from './GalenData/UserMFA.model';
import { UserRequestBody } from './GalenData/UserRequestBody.model';
import { UserRoleEnum } from './GalenData/UserRole.model';

type AlertType = 'success' | 'warning' | 'error' | 'info';

// ---- remote function wrapper ----
type callbackFunction = (result: boolean) => void;

const isClient = (): boolean => {
  return window._cdvElectronIpc !== undefined;
};

const clientFunction = (func: string, ...args: any[]): any => {
  if (isClient()) {
    if (window._cdvElectronIpc[func] === undefined) {
      console.log('Error: client api not found', func);
    } else {
      const ret = window._cdvElectronIpc[func](...args);

      // return Promise
      return ret instanceof Promise ? ret : Promise.resolve(ret);
    }
  } else {
    console.log('Error: calling client api in non-client environment', func);
  }
};

const convertExamToSession = async (exam: DeviceDataView<ExamData>) => {
  const user = await getUserFromUserId(exam.data?.PracticeUserId?.value);
  const username = user?.emailAddress;

  return await transformExamToSession(exam, username);
};

export const getUserFromUserId = async (userId: string) => {
  try {
    const res = await httpRequest.get<
      GalenDataUser,
      AxiosResponse<GalenDataUser>,
      getByFiltersUsingGET9
    >(ApiUrlsEnum.USER + `/${userId}`);

    return res.data;
  } catch (error) {
    if (
      isAxiosError(error) &&
      error.response &&
      error.response.status === 404
    ) {
      return;
    }
    throw error;
  }
};

// ---- api ----
const Api = {
  alertBox({
    title,
    message,
    callback,
    locale,
    type = 'success',
  }: {
    title: string;
    message: string;
    callback?: callbackFunction;
    locale: string;
    type: AlertType;
  }) {
    const div = document.createElement('div');
    const root = createRoot(div);

    const getIcon = () => {
      switch (type) {
        case 'success':
          return createElement(FontAwesomeIcon, {
            icon: faCheckCircle,
            className: 'text-success',
            size: 'lg',
          });
        case 'warning':
          return createElement(FontAwesomeIcon, {
            icon: faExclamationCircle,
            className: 'text-warning',
            size: 'lg',
          });
        case 'error':
          return createElement(FontAwesomeIcon, {
            icon: faTimesCircle,
            className: 'text-danger',
            size: 'lg',
          });
        case 'info':
          return createElement(FontAwesomeIcon, {
            icon: faInfoCircle,
            className: 'text-info',
            size: 'lg',
          });

        default:
          return null;
      }
    };

    // Render the icon
    flushSync(() => {
      root.render(getIcon());
    });

    window.bootbox.alert({
      title: `${div.innerHTML} ${title}`,
      message,
      locale,
      callback,
      className: `bootbox-${type}`,
    });
  },

  confirmBox({
    title,
    message,
    locale,
    buttons,
    callback,
  }: {
    title: string;
    message: string;
    locale: string;
    buttons?: BootboxConfirmPromptButtonMap;
    callback?: callbackFunction;
  }) {
    const div = document.createElement('div');
    const root = createRoot(div);

    const getIcon = () =>
      createElement(FontAwesomeIcon, {
        icon: faExclamationCircle,
        className: 'text-warning',
        size: 'lg',
      });

    flushSync(() => {
      root.render(getIcon());
    });

    window.bootbox.confirm({
      title: `${div.innerHTML} ${title}`,
      message,
      locale,
      buttons: {
        confirm: buttons?.confirm,
        cancel: {
          className: 'btn-primary-100',
          ...buttons?.cancel,
        },
      },
      callback,
    });
  },

  formatException(err: string): string {
    if (err.endsWith(`')`)) {
      return (
        'Error: ' + err.slice(err.indexOf(`'`) + 1, -2).replace('Error: ', '')
      );
    } else if (err.endsWith(`")`)) {
      return (
        'Error: ' + err.slice(err.indexOf(`"`) + 1, -2).replace('Error: ', '')
      );
    } else {
      return err;
    }
  },

  async getLocalIpAddress(): Promise<string[]> {
    return await clientFunction('getIpAddress');
  },

  remote: {
    get_user_by_email: async (emailAddress: string) => {
      const res = await httpRequest.get<
        PageOfUser,
        AxiosResponse<PageOfUser>,
        getByFiltersUsingGET9
      >(ApiUrlsEnum.USER, {
        params: {
          emailAddress,
        },
      });

      return res.data.content?.[0];
    },
    login: async (username: string, password: string) => {
      return await httpRequest.post<
        GalenDataUser,
        AxiosResponseWithRefreshToken<GalenDataUser>,
        LoginUsingPOST
      >(
        ApiUrlsEnum.LOGIN,
        {
          emailAddress: username,
          password,
        },
        {
          headers: {
            'Content-Type': ContentTypeEnum.URL_ENCODED,
          },
        },
      );
    },
    oauth2Login: async ({ code, loginType, redirectUri }: Oauth2LoginPost) => {
      return await httpRequest.post<
        GalenDataUser,
        AxiosResponseWithRefreshToken<GalenDataUser>,
        Oauth2LoginPost
      >(ApiUrlsEnum.OAUTH2_LOGIN, {
        code,
        loginType,
        redirectUri,
      });
    },
    verifyMFACode: async (code: string, xRequestToken: string) => {
      return await httpRequest.post<
        GalenDataUser,
        AxiosResponseWithRefreshToken<GalenDataUser>,
        SendMFACodePost
      >(ApiUrlsEnum.VERIFY_MFA_CODE, undefined, {
        params: {
          code,
        },
        headers: {
          'X-REQUEST-TOKEN': xRequestToken,
        },
      });
    },

    sendMFACode: async (xRequestToken: string) => {
      return await httpRequest.post<
        GalenDataUser,
        AxiosResponseWithRefreshToken<GalenDataUser>
      >(ApiUrlsEnum.SEND_MFA_CODE, undefined, {
        headers: {
          'X-REQUEST-TOKEN': xRequestToken,
        },
      });
    },

    sendResetCode: async (email: string) => {
      return await httpRequest.post<undefined, AxiosResponse<undefined>>(
        ApiUrlsEnum.SEND_RESET_CODE,
        null,
        {
          params: {
            // Double-encode the email to match the encoding logic used by the Galen Data portal.
            email: encodeURIComponent(email),
          } satisfies { email: string },
        },
      );
    },

    resetPassword: async (
      email: string,
      otp: string,
      newPassword: string,
      confirmPassword: string,
    ) => {
      return await httpRequest.post<
        undefined,
        AxiosResponseWithRefreshToken<undefined>,
        {
          emailAddress: string;
          otpCode: string;
          newPassword: string;
          confirmPassword: string;
        }
      >(ApiUrlsEnum.RESET_PASSWORD, {
        emailAddress: email,
        otpCode: otp,
        newPassword,
        confirmPassword,
      });
    },
    logout: async (reloadPage: boolean) => {
      useRemoteBoundStore.persist.clearStorage();

      if (reloadPage) {
        // set timestamp
        const url = new Url('/', true);
        url.query.ts = Date.now().toString();

        // reload page
        const newUrl = url.toString();
        window.location.href = newUrl;
      }
      return true;
    },

    getUserSettings: async (userId: string) => {
      return await Api.remote._get_custom_field_data<CustomUserProfileFieldEnum>(
        userId,
      );
    },

    getPracticeSettings: async (practiceId: string) => {
      return await Api.remote._get_custom_field_data<CustomPracticeProfileFieldEnum>(
        practiceId,
      );
    },

    _get_custom_field_definitions: async <
      T extends
        | CustomPracticeProfileFieldEnum
        | CustomUserProfileFieldEnum
        | CustomPatientProfileFieldEnum,
    >(
      fieldArea: string,
    ) => {
      try {
        return await httpRequest.get<
          PageOfCustomField<T>,
          AxiosResponse<PageOfCustomField<T>>,
          null
        >(ApiUrlsEnum.USER_CUSTOM_FIELD, {
          params: { fieldArea },
        });
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return {
            data: {
              content: [],
              totalElements: 0,
            },
          };
        }
        throw error;
      }
    },

    getUserCustomFieldDefinitions: async () => {
      return await Api.remote._get_custom_field_definitions<CustomUserProfileFieldEnum>(
        CustomFieldAreaEnum.UserProfile,
      );
    },

    getPracticeCustomFieldDefinitions: async () => {
      return await Api.remote._get_custom_field_definitions<CustomPracticeProfileFieldEnum>(
        CustomFieldAreaEnum.PracticeProfile,
      );
    },

    getPatientCustomFieldDefinitions: async () => {
      return await Api.remote._get_custom_field_definitions<CustomPatientProfileFieldEnum>(
        CustomFieldAreaEnum.UserProfile,
      );
    },

    _update_user_custom_fields: async (
      customData: {
        field: CustomField<CustomUserProfileFieldEnum> | undefined;
        fieldData: string | string[] | undefined | null;
      }[],
      user: GalenDataUser,
    ) => {
      return await httpRequest.put<UserRequestBody>(ApiUrlsEnum.USER_CUSTOM, {
        customData,
        user,
      });
    },

    _update_practice_custom_fields: async (
      customData: {
        field: CustomField<CustomPracticeProfileFieldEnum> | undefined;
        fieldData: string | boolean;
      }[],
      practice: PracticeAssociation,
    ) => {
      return await httpRequest.put<UserRequestBody>(
        ApiUrlsEnum.PRACTICE_CUSTOM,
        {
          customData,
          practice,
        },
      );
    },
    saveUserSettings: async ({
      userSettingsData,
      data,
      user,
      userCustomFields,
    }: {
      userSettingsData: CustomFieldData<CustomUserProfileFieldEnum>[];
      data: Partial<{
        [key in CustomUserProfileFieldEnum]: string | string[] | null;
      }>;
      user: GalenDataUser;
      userCustomFields?: CustomField<CustomUserProfileFieldEnum>[];
    }) => {
      return await updateUserSettings({
        userSettingsData,
        data,
        user,
        userCustomFields,
      });
    },

    savePracticeSettings: async (
      practiceDirtyFields: Array<keyof SettingFormDataTypes>,
      practiceSettingsData:
        | CustomFieldData<CustomPracticeProfileFieldEnum>[]
        | undefined,
      data: SettingFormDataTypes,
      practice: PracticeAssociation,
      practiceCustomFields?: CustomField<CustomPracticeProfileFieldEnum>[],
    ) => {
      return await updatePracticeSettings(
        practiceDirtyFields,
        practiceSettingsData,
        data,
        practice,
        practiceCustomFields,
      );
    },

    create_user: async (data: AddUserRequestBody) => {
      return await httpRequest.post<
        undefined,
        AxiosResponse<undefined>,
        AddUserRequestBody
      >(ApiUrlsEnum.USER, data);
    },

    update_user: async (data: GalenDataUser) => {
      return await httpRequest.put<
        undefined,
        AxiosResponse<undefined>,
        GalenDataUser
      >(ApiUrlsEnum.USER, data);
    },

    upload_media_file: async ({
      deviceDataModelId,
      deviceDataId,
      propertyCode,
      imageUrl,
      imageFileName,
    }: {
      deviceDataModelId: string;
      deviceDataId: string;
      propertyCode: string;
      imageUrl: string;
      imageFileName: string;
    }) => {
      const formData = new FormData();

      // Needs cache no store to fix CORS issue.
      // Hypothesis, prevents two request from merging
      const imageBlob = await fetch(imageUrl, { cache: 'no-store' }).then(
        (r) => {
          return r.blob();
        },
      );

      formData.append('deviceDataModelId', deviceDataModelId);
      formData.append('propertyCode', propertyCode);
      formData.append('data', imageBlob, imageFileName);

      await Api.remote._save_media_file(deviceDataId, formData);
    },

    _save_device_data_record: async <T>(data: DeviceDataSaveRequestBody<T>) => {
      return await httpRequest.post(ApiUrlsEnum.DEVICE_DATA, data);
    },

    _save_media_file: async (deviceDataId: string, data: FormData) => {
      return await httpRequest.post(
        `${ApiUrlsEnum.DEVICE_DATA}/${deviceDataId}`,
        data,
        {
          headers: {
            'Content-Type': ContentTypeEnum.MULTIPART,
          },
        },
      );
    },

    _get_custom_field_data: async <T>(customFieldId: string) => {
      return await httpRequest.get<
        CustomFieldData<T>[],
        AxiosResponse<CustomFieldData<T>[]>,
        string
      >(ApiUrlsEnum.USER_CUSTOM_FIELD_DATA + `/${customFieldId}`);
    },

    get_custom_field_multiple_entities: async <T>(
      data: CustomDataRequestBody,
    ) => {
      try {
        return await httpRequest.post<
          PageOfDeviceData<T>,
          AxiosResponse<Record<string, CustomFieldData<T>[]>>,
          CustomDataRequestBody
        >(ApiUrlsEnum.USER_CUSTOM_FIELD_DATA, data);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return {
            data: undefined,
          };
        }
        throw error;
      }
    },

    _get_all_device_data: async (params?: DeviceDataGetFilter) => {
      return await httpRequest.get<
        PageOfDeviceData<AboutData>,
        AxiosResponse<PageOfDeviceData<AboutData>>,
        DeviceDataGetFilter
      >(ApiUrlsEnum.DEVICE_DATA, {
        params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      });
    },

    _get_device_data: async <T>({
      deviceDataModelId,
      deviceDataId,
    }: {
      deviceDataModelId: string;
      deviceDataId: string;
    }) => {
      return await httpRequest.get<T>(
        `/data/devicedata/${deviceDataModelId}/${deviceDataId}`,
      );
    },

    _count_device_data: async (data: DeviceDataGetRequestBody) => {
      return await httpRequest.post<
        number,
        AxiosResponse<number>,
        DeviceDataGetRequestBody
      >(ApiUrlsEnum.COUNT_DEVICE_DATA, data);
    },

    _get_device_data_advanced: async <T>(data: DeviceDataGetRequestBody) => {
      return await httpRequest.post<
        PageOfDeviceData<T>,
        AxiosResponse<PageOfDeviceData<T>>,
        DeviceDataGetRequestBody
      >(ApiUrlsEnum.DEVICE_DATA_ADVANCED, data);
    },

    get_site_data: async () => {
      try {
        return await Api.remote._get_device_data_advanced_owner_custom_criteria<SiteData>(
          {
            deviceDataModelId: import.meta.env.VITE_SITE_DEVICE_DATA_MODEL_ID,
          },
        );
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return {
            data: {
              content: [],
              totalElements: 0,
            },
          };
        }
        throw error;
      }
    },

    _get_device_data_advanced_owner_custom_criteria: async <T>(
      data: DeviceDataGetRequestBodyCriteriaGroup,
      params?: Omit<
        DeviceDataGetFilter,
        'deviceDataModelId' | 'rangeStartDateTime' | 'rangeEndDateTime'
      >,
    ) => {
      return await httpRequest.post<
        PageOfDeviceDataView<T>,
        AxiosResponse<PageOfDeviceDataView<T>>,
        DeviceDataGetRequestBody
      >(ApiUrlsEnum.DEVICE_DATA_ADVANCED_OWNER_CUSTOM_CRITERIA, data, {
        params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      });
    },

    get_exams: async ({
      deviceDataModelId,
      ownerFilter,
      groupElements,
      params,
    }: {
      deviceDataModelId: string;
      ownerFilter: OwnerFilter;
      groupElements: CriteriaGroupGroupElementsInner[];
      params?: Omit<
        DeviceDataGetFilter,
        'deviceDataModelId' | 'rangeStartDateTime' | 'rangeEndDateTime'
      >;
    }) => {
      try {
        return await Api.remote._get_device_data_advanced_owner_custom_criteria<ExamData>(
          {
            deviceDataModelId,
            deviceCriteriaGroup: {
              groupElements,
            },
            ownerFilter,
          },
          params,
        );
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return {
            data: {
              content: undefined,
              totalElements: 0,
            },
          };
        }
        throw error;
      }
    },
    get_hidden_exams: async ({
      deviceDataModelId,
      users,
      groupElements,
    }: {
      deviceDataModelId: string;
      users?: string[];
      groupElements?: CriteriaGroupGroupElementsInner[];
    }) => {
      return Api.remote.get_exams({
        deviceDataModelId,
        ownerFilter: {
          users,
        },
        groupElements: [
          {
            key: 'IsHidden',
            operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            value: true,
          },
          ...(groupElements ?? []),
        ],
      });
    },
    get_not_hidden_exams: async ({
      deviceDataModelId,
      users,
      siteId,
      params,
    }: {
      deviceDataModelId: string;
      users: string[];
      siteId?: string;
      params?: Omit<
        DeviceDataGetFilter,
        'deviceDataModelId' | 'rangeStartDateTime' | 'rangeEndDateTime'
      >;
    }) => {
      return Api.remote.get_exams({
        deviceDataModelId,
        ownerFilter: {
          users,
        },
        groupElements: [
          {
            key: 'IsHidden',
            operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            value: false,
          },
          {
            key: 'UploadStatusComplete',
            operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            value: true,
          },
          ...(siteId
            ? [
                {
                  key: 'SiteId',
                  operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
                  value: siteId,
                  isOr: false,
                },
              ]
            : []),
        ],
        params,
      });
    },

    update_exam_order: async (
      data: DeviceDataView<OrderData>,
      updateData: {
        status: OrderStatusEnum;
      },
    ) => {
      if (!data.deviceDataModelId || !data.deviceDataId) {
        throw new Error('Missing device data model id or device data id');
      }
      return await Api.remote._save_device_data_record<OrderSubmissionData>({
        deviceDataModelId: data.deviceDataModelId,
        devicePropertySetId: import.meta.env
          .VITE_DEVICE_PROPERTY_SET_ID_EXAM_ORDER,
        deviceDataId: data.deviceDataId,
        data: {
          Status: updateData.status,
          OrderStatusDatetime: moment(new Date()).toISOString(),
        },
      });
    },

    update_exam_order_task: async (
      deviceDataId: string,
      taskId: OrderTaskId,
      taskValue: boolean,
    ) => {
      return await Api.remote._save_device_data_record<OrderSubmissionData>({
        deviceDataId,
        deviceDataModelId: import.meta.env.VITE_EXAM_ORDER_MODEL_ID,
        devicePropertySetId: import.meta.env
          .VITE_DEVICE_PROPERTY_SET_ID_EXAM_ORDER,
        data: {
          [taskId]: taskValue,
        },
      });
    },

    // TODO: Because of unification of orderId and deviceDataId, this function should be refactored to look up order directly
    get_exam_order: async ({
      groupElements,
      ownerFilter,
      params,
    }: {
      groupElements?: CriteriaGroupGroupElementsInner[];
      ownerFilter?: OwnerFilter;
      params?: Omit<
        DeviceDataGetFilter,
        'deviceDataModelId' | 'rangeStartDateTime' | 'rangeEndDateTime'
      >;
    }) => {
      try {
        return await Api.remote._get_device_data_advanced_owner_custom_criteria<OrderData>(
          {
            deviceDataModelId: import.meta.env.VITE_EXAM_ORDER_MODEL_ID,
            deviceCriteriaGroup: {
              groupElements,
            },
            ownerFilter,
          },
          params,
        );
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return {
            data: {
              content: undefined,
              totalElements: 0,
              totalPages: 0,
            },
          };
        }
        throw error;
      }
    },

    get_device_data_aggregate: async (data: DeviceDataAggregateRequestBody) => {
      return await httpRequest.post<
        PageOfDeviceDataAggregateResult,
        AxiosResponse<PageOfDeviceDataAggregateResult>,
        DeviceDataAggregateRequestBody
      >(`${ApiUrlsEnum.DEVICE_DATA_AGGREGATE}`, data);
    },

    get_device_data_aggregate_advanced: async (
      data: DeviceDataMultiplePropertiesAggregateRequestBody,
    ) => {
      return await httpRequest.post<
        PageOfDeviceDataMultipleAggregate,
        AxiosResponse<PageOfDeviceDataMultipleAggregate>,
        DeviceDataMultiplePropertiesAggregateRequestBody
      >(`${ApiUrlsEnum.DEVICE_DATA_AGGREGATE_ADVANCED}`, data);
    },

    get_practice: async (practiceId: string) => {
      return await httpRequest.get<Practice, AxiosResponse<Practice>, string>(
        ApiUrlsEnum.PRACTICE + `/${practiceId}`,
      );
    },

    search_practice: async (params?: getByOrgFilter) => {
      return await httpRequest.get<
        PageOfPractice,
        AxiosResponse<PageOfPractice>
      >(ApiUrlsEnum.PRACTICE, {
        params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      });
    },

    search_supplier: async (params?: getByOrgFilter) => {
      return await httpRequest.get<
        PageOfPractice,
        AxiosResponse<PageOfPractice>
      >(ApiUrlsEnum.SUPPLIER, {
        params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      });
    },

    _load_patients: async (search: string) => {
      const patientParams: getByFiltersUsingGET9 = {};

      if (/^\d{4}-\d{2}-\d{2}$/.test(search)) {
        patientParams.dateOfBirth = search;
      } else if (/^\d+$/.test(search)) {
        patientParams.patientId = search;
      } else if (search !== '') {
        patientParams.nameLike = search;
      }

      patientParams.role = UserRoleEnum.Patient;

      return await Api.remote._get_all_users(patientParams);
    },
    _load_exams: async (
      deviceDataModelId: string,
      page: number,
      numberPerPage: number,
      sortOrder: SortOrder,
      userIds: string[],
      siteId?: string,
    ) => {
      const sortBy = ['data.ReportDatetime.value'];

      return await Api.remote.get_not_hidden_exams({
        deviceDataModelId,
        users: userIds,
        siteId,
        params: {
          pageNumber: page,
          pageSize: numberPerPage,
          sortBy,
          sortOrder,
        },
      });
    },

    find_retake_sessions: async (deviceDataModelId: string, iid: string) => {
      const parentExam = await httpRequest.get<
        DeviceDataView<ExamData>,
        AxiosResponse<DeviceDataView<ExamData>>,
        DeviceDataGetRequestBody
      >(`${ApiUrlsEnum.DEVICE_DATA_OWNER}/${deviceDataModelId}/${iid}`);

      let sessions: Session[] = [];

      if (!parentExam.data.ownerId) {
        return sessions;
      }

      const exams = await Api.remote.get_hidden_exams({
        deviceDataModelId,
        users: [parentExam.data.ownerId],
        groupElements: [
          {
            key: 'OrderId',
            operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            value: parentExam.data.data.OrderId.value,
          },
        ],
      });

      // convert to sessions
      if (exams.data.content !== undefined) {
        const sessionPromises = exams.data.content.map((exam) =>
          transformExamToSession(exam),
        );
        sessions = await Promise.all(sessionPromises);
      }

      const session = transformExamToSession(parentExam.data);
      sessions.unshift(session);

      return sessions;
    },

    load_session: async ({
      deviceDataId,
      deviceDataModelId,
    }: {
      deviceDataId: string;
      deviceDataModelId: string;
    }) => {
      try {
        const exam = await httpRequest.get<
          DeviceDataView<ExamData>,
          AxiosResponse<DeviceDataView<ExamData>>,
          DeviceDataGetRequestBody
        >(
          `${ApiUrlsEnum.DEVICE_DATA_OWNER}/${deviceDataModelId}/${deviceDataId}`,
        );

        const newSession = await convertExamToSession(exam.data);

        const orderId = newSession.orderId;

        const examOrder = await Api.remote.get_exam_order({
          groupElements: [
            {
              key: 'OrderId',
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
              value: orderId,
            },
          ],
        });

        const orderData = examOrder.data.content?.[0].data;

        newSession.order = orderData;
        newSession.template = orderData?.OrderType.value;

        return newSession;
      } catch (error) {
        console.log('load session error', error);
        throw error;
      }
    },

    save_session: async (
      iid: string,
      updateData: {
        completedReason: CompletedReasonEnum;
        completedTime: Date;
      },
      deviceDataModelId: string,
      devicePropertySetId: string,
    ) => {
      await Api.remote._save_device_data_record<{
        CompletedReason: CompletedReasonEnum;
        CompletedTime: Date;
      }>({
        deviceDataModelId,
        devicePropertySetId,
        deviceDataId: iid,
        data: {
          CompletedReason: updateData.completedReason,
          CompletedTime: updateData.completedTime,
        },
      });
    },

    change_password: async (
      userId: string,
      currentPassword: string,
      newPassword: string,
      forcePasswordReset: boolean = false,
    ) => {
      return await httpRequest.put<
        undefined,
        AxiosResponse<undefined>,
        UpdatePasswordRequestBody
      >(ApiUrlsEnum.CHANGE_PASSWORD, {
        userId,
        currentPassword,
        newPassword,
        newConfirmPassword: newPassword,
        forcePasswordReset,
      });
    },

    _get_all_users: async (params?: getByFiltersUsingGET9) => {
      try {
        return await httpRequest.get<
          PageOfUser,
          AxiosResponse<PageOfUser>,
          getByFiltersUsingGET9
        >(ApiUrlsEnum.USER, {
          params,
          paramsSerializer: (params) => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return {
            data: {
              content: [],
              totalElements: 0,
            },
          };
        }
        throw error;
      }
    },

    _get_users_advanced: async (
      data?: IGetUsersAdvancedRequestBody,
      params?: IGetUserAdvancedParams<GalenDataUser>,
    ) => {
      try {
        const requestData: IGetUsersAdvancedRequestBody = data || [];

        return await httpRequest.post<
          PageOfUser,
          AxiosResponse<PageOfUser>,
          IGetUsersAdvancedRequestBody
        >(ApiUrlsEnum.USER_ADVANCED, requestData, {
          params,
          paramsSerializer: (params) => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return {
            data: {
              content: [],
              totalElements: 0,
              totalPages: 0,
            },
          };
        }
        throw error;
      }
    },

    get_backend_state: async (sessionId: string) => {
      try {
        const data = await Api.remote._get_device_data<BackendStatus>({
          deviceDataModelId: import.meta.env.VITE_BACKEND_STATE_MACHINE_ID,
          deviceDataId: sessionId,
        });
        return data.data.data;
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return undefined;
        }
        throw error;
      }
    },

    delete_user: async (userId: string) => {
      return await httpRequest.delete<
        undefined,
        AxiosResponse<undefined>,
        string
      >(ApiUrlsEnum.USER, {
        params: {
          userId,
        },
      });
    },

    set_user_mfa: async (enable: boolean, userId: string) => {
      return await httpRequest.post<
        undefined,
        AxiosResponse<undefined>,
        UserMFA
      >(ApiUrlsEnum.USER_MFA, {
        enable,
        userId,
        modes: [MFAModeEnum.Email],
      });
    },
  },
};

(window as any).Api = Api;
export { Api };
